import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getContracts, getContract, createContract } from '../../services/api';

const DIVISIONS = ['Alpha Division', 'Beta Division', 'Gamma Division', 'Delta Division'];
const CONTRACT_TYPES = ['CPFF', 'T&M', 'FFP', 'CPIF'];
const YEARS = ['Base', 'OY1', 'OY2', 'OY3', 'OY4', 'OY5', 'OY6', 'OY7', 'OY8', 'OY9', 'Overall'];
const STATUSES = ['Proposed', 'Active', 'Exercised', 'Not Exercised', 'Overall'];

const EMPTY_FORM = {
  division: '',
  program: '',
  projectName: '',
  contractNumber: '',
  contractType: '',
  customer: '',
  endCustomer: '',
  year: '',
  status: 'Proposed',
  startDate: '',
  endDate: '',
  contractValue: '',
  fundingPerYear: '',
};

/* ── Reusable field components to reduce cognitive complexity ── */

function ReadOnlyOrSelect({ isReadOnly, value, options, placeholder, error, onChange }) {
  if (isReadOnly) {
    return <input type="text" className="form-input readonly" value={value} readOnly />;
  }
  return (
    <select
      className={`form-select${error ? ' error' : ''}`}
      value={value}
      onChange={onChange}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function TextInput({ isReadOnly, value, error, onChange, placeholder }) {
  return (
    <input
      type="text"
      className={`form-input${isReadOnly ? ' readonly' : ''}${error ? ' error' : ''}`}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={!!isReadOnly}
    />
  );
}

function FieldError({ error }) {
  if (!error) return null;
  return <span className="form-error">{error}</span>;
}

/* ── Project Selector ── */

function ProjectSelector({
  selectedProject, loadingContracts, searchQuery, setSearchQuery,
  showDropdown, setShowDropdown, filteredProjects, handleSelectProject,
  handleClearProject, searchRef, dropdownRef,
}) {
  if (selectedProject) {
    return (
      <div className="project-selected-banner">
        <span>
          {selectedProject.projectName} ({selectedProject.contractNumber})
        </span>
        <button onClick={handleClearProject}>Change</button>
      </div>
    );
  }

  return (
    <div className="project-search-wrapper">
      <input
        ref={searchRef}
        type="text"
        className="project-search-input"
        placeholder={loadingContracts ? 'Loading projects...' : 'Search by project name, contract #, or program...'}
        value={searchQuery}
        onChange={(e) => { setSearchQuery(e.target.value); setShowDropdown(true); }}
        onFocus={() => setShowDropdown(true)}
        disabled={loadingContracts}
      />
      {showDropdown && (
        <ProjectDropdown
          projects={filteredProjects}
          searchQuery={searchQuery}
          onSelect={handleSelectProject}
          dropdownRef={dropdownRef}
        />
      )}
    </div>
  );
}

function ProjectDropdown({ projects, searchQuery, onSelect, dropdownRef }) {
  return (
    <div className="project-dropdown" ref={dropdownRef} role="listbox">
      {projects.length > 0 ? (
        projects.map((p) => (
          <div
            key={p.contractNumber}
            className="project-dropdown-item"
            role="option"
            tabIndex={0}
            aria-selected={false}
            onClick={() => onSelect(p)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(p); } }}
          >
            <div className="project-dropdown-name">{p.projectName}</div>
            <div className="project-dropdown-meta">
              {p.contractNumber} &bull; {p.program} &bull; {p.division}
            </div>
          </div>
        ))
      ) : (
        <div className="project-dropdown-empty">
          {searchQuery ? 'No matching projects' : 'No projects available'}
        </div>
      )}
    </div>
  );
}

/* ── Form Sections ── */

function ContractIdentificationSection({ form, errors, isReadOnly, handleChange }) {
  return (
    <div className="form-section">
      <div className="form-section-title">Contract Identification</div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Division <span className="required">*</span></label>
          <ReadOnlyOrSelect
            isReadOnly={isReadOnly}
            value={form.division}
            options={DIVISIONS}
            placeholder="Select Division"
            error={errors.division}
            onChange={(e) => handleChange('division', e.target.value)}
          />
          <FieldError error={errors.division} />
        </div>

        <div className="form-group">
          <label className="form-label">Program <span className="required">*</span></label>
          <TextInput isReadOnly={isReadOnly} value={form.program} error={errors.program} onChange={(e) => handleChange('program', e.target.value)} placeholder="e.g. Yellowstone" />
          <FieldError error={errors.program} />
        </div>

        <div className="form-group">
          <label className="form-label">Project Name <span className="required">*</span></label>
          <TextInput isReadOnly={isReadOnly} value={form.projectName} error={errors.projectName} onChange={(e) => handleChange('projectName', e.target.value)} placeholder="e.g. Yellowstone - TO10" />
          <FieldError error={errors.projectName} />
        </div>

        <div className="form-group">
          <label className="form-label">Contract # <span className="required">*</span></label>
          <TextInput isReadOnly={isReadOnly} value={form.contractNumber} error={errors.contractNumber} onChange={(e) => handleChange('contractNumber', e.target.value)} placeholder="e.g. CNT-2024-001" />
          <FieldError error={errors.contractNumber} />
        </div>

        <div className="form-group">
          <label className="form-label">Contract Type <span className="required">*</span></label>
          <ReadOnlyOrSelect
            isReadOnly={isReadOnly}
            value={form.contractType}
            options={CONTRACT_TYPES}
            placeholder="Select Type"
            error={errors.contractType}
            onChange={(e) => handleChange('contractType', e.target.value)}
          />
          <FieldError error={errors.contractType} />
        </div>

        <div className="form-group">
          <label className="form-label">Customer <span className="required">*</span></label>
          <TextInput isReadOnly={isReadOnly} value={form.customer} error={errors.customer} onChange={(e) => handleChange('customer', e.target.value)} placeholder="e.g. US Army" />
          <FieldError error={errors.customer} />
        </div>

        <div className="form-group">
          <label className="form-label">End Customer</label>
          <TextInput isReadOnly={isReadOnly} value={form.endCustomer} onChange={(e) => handleChange('endCustomer', e.target.value)} placeholder="Optional" />
        </div>
      </div>
    </div>
  );
}

function PeriodDetailsSection({ form, errors, existingYears, handleChange }) {
  return (
    <div className="form-section">
      <div className="form-section-title">Period Details</div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Period / Year <span className="required">*</span></label>
          <select
            className={`form-select${errors.year ? ' error' : ''}`}
            value={form.year}
            onChange={(e) => handleChange('year', e.target.value)}
          >
            <option value="">Select Period</option>
            {YEARS.map((y) => (
              <option key={y} value={y}>
                {y}{existingYears.has(y) ? ' (exists)' : ''}
              </option>
            ))}
          </select>
          <FieldError error={errors.year} />
        </div>

        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-select" value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Start Date</label>
          <input type="date" className="form-input" value={form.startDate} onChange={(e) => handleChange('startDate', e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">End Date</label>
          <input type="date" className={`form-input${errors.endDate ? ' error' : ''}`} value={form.endDate} onChange={(e) => handleChange('endDate', e.target.value)} />
          <FieldError error={errors.endDate} />
        </div>
      </div>
    </div>
  );
}

function FinancialDataSection({ form, errors, handleChange, calculatedTotalITD, itdPlaceholder }) {
  return (
    <div className="form-section">
      <div className="form-section-title">Financial Data</div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Contract Value ($)</label>
          <input type="text" className={`form-input${errors.contractValue ? ' error' : ''}`} value={form.contractValue} onChange={(e) => handleChange('contractValue', e.target.value)} placeholder="e.g. 1,500,000" />
          <FieldError error={errors.contractValue} />
        </div>

        <div className="form-group">
          <label className="form-label">Funding Per PoP ($)</label>
          <input type="text" className={`form-input${errors.fundingPerYear ? ' error' : ''}`} value={form.fundingPerYear} onChange={(e) => handleChange('fundingPerYear', e.target.value)} placeholder="e.g. 500,000" />
          <FieldError error={errors.fundingPerYear} />
        </div>

        <div className="form-group">
          <label className="form-label">Total Funding ITD ($)</label>
          <input
            type="text"
            className="form-input readonly"
            value={calculatedTotalITD > 0 ? calculatedTotalITD.toLocaleString('en-US') : ''}
            readOnly
            placeholder={itdPlaceholder}
          />
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ── */

function NewContract({ onNavigate }) {
  const [mode, setMode] = useState('existing'); // 'existing' or 'new'
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Project selector state
  const [allContracts, setAllContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectPeriods, setProjectPeriods] = useState([]);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Fetch contracts on mount for existing project selector
  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      try {
        const data = await getContracts();
        if (!cancelled) setAllContracts(data);
      } catch {
        // silently fail — user can still use "New Project" mode
      } finally {
        if (!cancelled) setLoadingContracts(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  // Deduplicate contracts into unique projects (by contractNumber)
  const uniqueProjects = useMemo(() => {
    const map = new Map();
    allContracts.forEach((c) => {
      if (!map.has(c.contractNumber)) {
        map.set(c.contractNumber, {
          contractNumber: c.contractNumber,
          projectName: c.projectName || '',
          program: c.program || '',
          division: c.division || '',
          contractType: c.contractType || '',
          customer: c.customer || '',
          endCustomer: c.endCustomer || '',
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.projectName.localeCompare(b.projectName)
    );
  }, [allContracts]);

  // Get existing years for the selected project (to warn about duplicates)
  const existingYears = useMemo(() => {
    if (!selectedProject) return new Set();
    if (projectPeriods.length > 0) {
      return new Set(projectPeriods.map((p) => p.year));
    }
    return new Set(
      allContracts
        .filter((c) => c.contractNumber === selectedProject.contractNumber)
        .map((c) => c.year)
    );
  }, [selectedProject, projectPeriods, allContracts]);

  // Sum prior funded amounts from existing periods (excluding "Overall")
  const priorFundedITD = useMemo(() => {
    if (!selectedProject || projectPeriods.length === 0) return 0;
    return projectPeriods
      .filter((p) => p.year !== 'Overall')
      .reduce((sum, p) => sum + (Number(p.fundingPerYear) || 0), 0);
  }, [selectedProject, projectPeriods]);

  // Auto-calculate Total Funding ITD = prior funded + new Funding Per PoP
  const newFunding = Number((form.fundingPerYear || '').replace(/,/g, '')) || 0;
  const calculatedTotalITD = (mode === 'existing' && selectedProject)
    ? priorFundedITD + newFunding
    : newFunding;

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return uniqueProjects;
    const q = searchQuery.toLowerCase();
    return uniqueProjects.filter(
      (p) =>
        p.projectName.toLowerCase().includes(q) ||
        p.contractNumber.toLowerCase().includes(q) ||
        p.program.toLowerCase().includes(q)
    );
  }, [uniqueProjects, searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        searchRef.current && !searchRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleModeChange(newMode) {
    setMode(newMode);
    setForm({ ...EMPTY_FORM });
    setErrors({});
    setApiError('');
    setSelectedProject(null);
    setProjectPeriods([]);
    setSearchQuery('');
    setShowDropdown(false);
  }

  async function handleSelectProject(project) {
    setSelectedProject(project);
    setShowDropdown(false);
    setSearchQuery('');
    setForm((prev) => ({
      ...prev,
      division: project.division,
      program: project.program,
      projectName: project.projectName,
      contractNumber: project.contractNumber,
      contractType: project.contractType,
      customer: project.customer,
      endCustomer: project.endCustomer || '',
    }));
    setErrors({});

    // Fetch all periods for this project to calculate prior funded ITD
    try {
      const periods = await getContract(project.contractNumber);
      setProjectPeriods(Array.isArray(periods) ? periods : []);
    } catch {
      setProjectPeriods([]);
    }
  }

  function handleClearProject() {
    setSelectedProject(null);
    setProjectPeriods([]);
    setForm({ ...EMPTY_FORM });
    setSearchQuery('');
    setErrors({});
  }

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  function validate() {
    const errs = {};
    const required = ['division', 'program', 'projectName', 'contractNumber', 'contractType', 'customer', 'year'];
    required.forEach((f) => {
      if (!form[f] || !form[f].trim()) {
        errs[f] = 'Required';
      }
    });

    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      errs.endDate = 'Must be after start date';
    }

    ['contractValue', 'fundingPerYear'].forEach((f) => {
      if (form[f] && isNaN(Number(form[f].replace(/,/g, '')))) {
        errs[f] = 'Must be a number';
      }
    });

    if (mode === 'existing' && selectedProject && form.year && existingYears.has(form.year)) {
      errs.year = `${form.year} already exists for this project`;
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;

    const payload = { ...form };
    // Convert numeric fields
    ['contractValue', 'fundingPerYear'].forEach((f) => {
      if (payload[f]) {
        payload[f] = Number(payload[f].replace(/,/g, ''));
      } else {
        delete payload[f];
      }
    });
    // Use auto-calculated Total Funding ITD
    if (calculatedTotalITD > 0) {
      payload.totalFundingITD = calculatedTotalITD;
    } else {
      delete payload.totalFundingITD;
    }
    // Remove empty optional strings
    if (!payload.endCustomer) delete payload.endCustomer;
    if (!payload.startDate) delete payload.startDate;
    if (!payload.endDate) delete payload.endDate;

    setSubmitting(true);
    try {
      await createContract(payload);
      onNavigate('contracts');
    } catch (err) {
      setApiError(err.message || 'Failed to create contract');
    } finally {
      setSubmitting(false);
    }
  }

  const isReadOnly = mode === 'existing' && selectedProject;

  const itdPlaceholder = (mode === 'existing' && selectedProject)
    ? `Prior: ${priorFundedITD.toLocaleString('en-US')} + new PoP funding`
    : 'Auto-calculated from Funding Per PoP';

  return (
    <div className="form-page">
      <button className="form-back-link" onClick={() => onNavigate('contracts')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to Contracts
      </button>

      <div className="form-card">
        {/* Mode Toggle */}
        <div className="project-toggle">
          <button
            className={`project-toggle-btn${mode === 'existing' ? ' active' : ''}`}
            onClick={() => handleModeChange('existing')}
          >
            Add to Existing Project
          </button>
          <button
            className={`project-toggle-btn${mode === 'new' ? ' active' : ''}`}
            onClick={() => handleModeChange('new')}
          >
            New Project
          </button>
        </div>

        {/* Project Selector (existing mode) */}
        {mode === 'existing' && (
          <div className="project-selector">
            <ProjectSelector
              selectedProject={selectedProject}
              loadingContracts={loadingContracts}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              showDropdown={showDropdown}
              setShowDropdown={setShowDropdown}
              filteredProjects={filteredProjects}
              handleSelectProject={handleSelectProject}
              handleClearProject={handleClearProject}
              searchRef={searchRef}
              dropdownRef={dropdownRef}
            />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <ContractIdentificationSection form={form} errors={errors} isReadOnly={isReadOnly} handleChange={handleChange} />
          <PeriodDetailsSection form={form} errors={errors} existingYears={existingYears} handleChange={handleChange} />
          <FinancialDataSection form={form} errors={errors} handleChange={handleChange} calculatedTotalITD={calculatedTotalITD} itdPlaceholder={itdPlaceholder} />

          {/* API Error */}
          {apiError && (
            <div className="form-api-error">{apiError}</div>
          )}

          {/* Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => onNavigate('contracts')}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Contract'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NewContract;
